/**
 * Web Push para la PWA: registro del service worker, permiso de
 * notificaciones y suscripción contra el backend (VAPID).
 * La suscripción solo se pide desde un gesto del usuario (menú de
 * notificaciones); si el permiso ya está concedido, se sincroniza sola.
 */
import { DataService } from './http-interceptor.service';

const SW_URL = '/sw.js';

export const isPushSupported = (): boolean =>
  typeof window !== 'undefined' &&
  'serviceWorker' in navigator &&
  'PushManager' in window &&
  'Notification' in window;

const urlBase64ToUint8Array = (base64String: string): Uint8Array => {
  const padding = '='.repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');
  const rawData = window.atob(base64);
  const outputArray = new Uint8Array(rawData.length);
  for (let i = 0; i < rawData.length; i += 1) {
    outputArray[i] = rawData.charCodeAt(i);
  }
  return outputArray;
};

const registerServiceWorker = async (): Promise<ServiceWorkerRegistration | null> => {
  try {
    return await navigator.serviceWorker.register(SW_URL);
  } catch (error) {
    console.warn('No se pudo registrar el service worker:', error);
    return null;
  }
};

const sendSubscriptionToBackend = async (subscription: PushSubscription): Promise<void> => {
  const json = subscription.toJSON();
  await DataService.post('push-subscriptions', {
    endpoint: json.endpoint,
    keys: json.keys,
    userAgent: navigator.userAgent,
  });
};

/**
 * Solicita permiso (debe llamarse desde un gesto del usuario), registra el
 * service worker y guarda la suscripción en el backend.
 * @returns permission final: 'granted' | 'denied' | 'default'
 */
export const subscribeToPush = async (): Promise<NotificationPermission> => {
  if (!isPushSupported()) {
    return 'denied';
  }

  let permission = Notification.permission;
  if (permission === 'default') {
    permission = await Notification.requestPermission();
  }
  if (permission !== 'granted') {
    return permission;
  }

  const registration = await registerServiceWorker();
  if (!registration) {
    return 'denied';
  }

  const response = await DataService.get('push-subscriptions/vapid-public-key');
  const publicKey: string | null = response.data?.publicKey || null;
  if (!publicKey) {
    console.warn('El servidor no tiene llaves VAPID configuradas');
    return 'default';
  }

  let subscription = await registration.pushManager.getSubscription();
  if (!subscription) {
    subscription = await registration.pushManager.subscribe({
      userVisibleOnly: true,
      applicationServerKey: urlBase64ToUint8Array(publicKey),
    });
  }

  await sendSubscriptionToBackend(subscription);
  return permission;
};

/**
 * Sincronización silenciosa al iniciar sesión: si el usuario ya concedió el
 * permiso, asegura que exista suscripción vigente en el backend (cubre
 * reinstalaciones, cambios de llaves o fallos previos del POST). Nunca pide
 * permiso ni lanza error.
 * @returns true si la suscripción quedó registrada en el backend
 * (o no hizo falta tocar nada); false si el permiso está concedido pero el
 * registro falló — la UI usa esto para ofrecer reactivar.
 */
export const syncPushSubscriptionIfGranted = async (): Promise<boolean> => {
  try {
    if (isPushSupported() && Notification.permission === 'granted') {
      await subscribeToPush();
      return true;
    }
  } catch (error) {
    console.warn('No se pudo sincronizar la suscripción push:', error);
  }
  return false;
};
